import * as _ from 'lodash';
import {
    ImageryProviderParameterType,
    LayerParameterDefinition,
    MapLayerOptionsHelper,
} from 'app/overview/config/data/MapLayerOptionsHelper';
import { IMapLayerProvider } from 'app/config/IMapLayerProvider';
import { generateUUID } from 'app/shared/uuid.service';
import MapService from 'app/shared/utilities/imagery/map_service';

type QueryParameters = { [key: string]: any };

export interface ImageryProviderParameter {
    key: string;
    value: string | boolean | number | string[];
    type: ImageryProviderParameterType;
    // For the case of custom fields it's possible to have duplicate keys.
    // for these, use a field ID aka a uuid. Just using a different name to avoid
    // confusion of the provider uuid
    // This field is temporary and does not get saved into the database.
    fieldId?: string;
    childProps?: any;
}

/**
 * This is an interesting one.
 * This layer interface is used in BaseImageryLayerService and ImsImageryLayerService. There's some important
 * points to note:
 *
 * 1. There will almost certainly be EXTRA fields on top of this. e.g. 'cesiumIonId' or 'mapId'. Extra fields
 * that are required for specific layer types are jammed on. Be careful!
 */
interface BaseImageryLayersServiceLayerObj {
    name: string;
    type: string;
    url?: string;
    queryParameters: any;
    uuid: string;
    hideInMapLayers: boolean;
    showByDefault: boolean;
    proxyPrefixUrl: string;
}

/**
 * This is the data structure that gets saved to the database
 */
interface JSImageryProvider {
    name: string;

    url?: string;

    type?: string;
    hideInMapLayers: boolean;
    showByDefault: boolean;
    relativeUrl?: boolean;
    uuid: string;
    proxyPrefixUrl?: string;

    // The base cesium options object
    stdParameters?: QueryParameters;

    // The queryParameters object inside of the base cesium object.
    queryParameters?: QueryParameters;
}

export class ImsImageryProvider implements IMapLayerProvider {
    private _uuid: string;
    private _name: string;
    private _type: string;
    private _service: MapService;
    private _hideInMapLayers: boolean;
    private _showByDefault: boolean;
    private _relativeUrl: boolean;
    private _proxyPrefixUrl: string;
    private _proxyPrefixEnabled: boolean;

    private _requiredParameters: ImageryProviderParameter[] = [];
    private _optionalParameters: ImageryProviderParameter[] = [];
    private _customParameters: ImageryProviderParameter[] = [];

    private readonly _imagMagOptionsHelper: MapLayerOptionsHelper;

    /**
     *
     * @param {JSImageryProvider} obj plain old javascript object used to create
     * this provider
     * @param {MapLayerOptionsHelper} imagMapOptionsHelper
     */
    constructor(obj: JSImageryProvider, imagMapOptionsHelper: MapLayerOptionsHelper) {
        if (!obj) {
            obj = {} as any;
        }

        this._imagMagOptionsHelper = imagMapOptionsHelper;

        // Apply defaults
        obj = Object.assign(
            {},
            {
                name: 'New Imagery Layer',
                uuid: generateUUID(),
                type: this._imagMagOptionsHelper.getDefaultType(),
            },
            obj,
        );

        this._name = obj.name;
        this._showByDefault = obj.showByDefault;
        this._hideInMapLayers = obj.hideInMapLayers ? true : false; // Make sure it ends up as a boolean
        // Make sure to use the private setter here
        // the public setter will change up the required / optional / custom params
        this._type = obj.type;
        this._uuid = obj.uuid;
        this._relativeUrl = obj.relativeUrl;
        this._proxyPrefixUrl = obj.proxyPrefixUrl;
        this._proxyPrefixEnabled =
            !_.isNil(this._proxyPrefixUrl) || !_.isEmpty(this._proxyPrefixUrl);

        if (!this._imagMagOptionsHelper.isValidType(this._type)) {
            throw new Error(`type not handled ${this._type}`);
        }

        const requiredOptions = this._imagMagOptionsHelper.getRequiredParams(this._type);
        const requiredOptionKeys = this._imagMagOptionsHelper.getRequiredParamKeys(this._type);
        const optionalOptions = this._imagMagOptionsHelper.getOptionalParams(this._type);
        const optionalOptionKeys = this._imagMagOptionsHelper.getOptionalParamKeys(this._type);

        // Handle required parameters that are directly on the object (legacy case)
        this._requiredParameters = requiredOptions.reduce(
            (accum: ImageryProviderParameter[], p: LayerParameterDefinition) => {
                if (!_.isNil(obj[p.mapParam])) {
                    accum.push({
                        key: p.mapParam,
                        value: obj[p.mapParam],
                        type: p.type as any,
                    } as any);
                }
                return accum;
            },
            [],
        );

        // Handle optional parameters that are directly on the object (legacy case)
        this._optionalParameters = optionalOptions.reduce(
            (accum: ImageryProviderParameter[], p: LayerParameterDefinition) => {
                if (!_.isNil(obj[p.mapParam])) {
                    accum.push({
                        key: p.mapParam,
                        value: obj[p.mapParam],
                        type: p.type,
                    } as any);
                }
                return accum;
            },
            [],
        );

        const stdParams = obj.stdParameters ? obj.stdParameters : {};

        // Handle required and optional parameters that are on the stdParameters object.
        // This maps to the cesium imagery layer options object
        Object.keys(stdParams).forEach((key) => {
            if (requiredOptionKeys.includes(key)) {
                const optDef = requiredOptions.find((o) => o.mapParam === key);
                this._requiredParameters.push({
                    key: key,
                    value: stdParams[key],
                    type: optDef.type,
                });
            } else if (optionalOptionKeys.includes(key)) {
                const optDef = optionalOptions.find((o) => o.mapParam === key);
                this._optionalParameters.push({
                    key: key,
                    value: stdParams[key],
                    type: optDef.type,
                });
            }
        });

        let queryParams = obj.queryParameters ? obj.queryParameters : {};

        // Handle custom parameters in the queryParameters object.
        // This maps to the cesium imagery layer queryParameters key inside the standard
        // cesium imagery layer options object.
        this._customParameters = Object.keys(queryParams).map((k) => {
            return ImsImageryProvider._createCustomParameter(k, queryParams[k]);
        });

        // Add in any missing required params...
        const defaultRequiredParams = this._createRequiredParamsForType(this._type);
        defaultRequiredParams.forEach((p, index) => {
            if (!this._requiredParameters.find((innerP) => innerP.key === p.key)) {
                // Use splice to make sure the required params are in the correct order
                this._requiredParameters.splice(index, 0, p);
            }
        });
    }

    toJS(): JSImageryProvider {
        return {
            name: this._name,
            uuid: this._uuid,
            type: this._type,
            proxyPrefixUrl: this.proxyPrefixButtonEnabled ? this._proxyPrefixUrl : undefined,
            hideInMapLayers: this._hideInMapLayers,
            showByDefault: this._showByDefault,
            relativeUrl: this._relativeUrl,
            stdParameters: Object.assign(
                this._optionalParameters.reduce(parameterReducer, {}),
                this._requiredParameters.reduce(parameterReducer, {}),
            ),
            queryParameters: Object.assign(this._customParameters.reduce(parameterReducer, {})),
        };
    }

    get relativeUrl(): boolean {
        return this._relativeUrl;
    }

    set relativeUrl(val: boolean) {
        this._relativeUrl = val;
    }

    get name(): string {
        return this._name;
    }

    get hideInMapLayers(): boolean {
        return this._hideInMapLayers;
    }

    get showByDefault(): boolean {
        return this._showByDefault;
    }

    get type(): string {
        return this._type;
    }
    get service(): MapService {
        return this._service;
    }

    get uuid(): string {
        return this._uuid;
    }

    get proxyPrefixUrl(): string {
        return this._proxyPrefixUrl;
    }

    set proxyPrefixUrl(val: string) {
        this._proxyPrefixUrl = val;
    }

    set proxyPrefixButtonEnabled(val: boolean) {
        this._proxyPrefixEnabled = val;
    }

    get proxyPrefixButtonEnabled(): boolean {
        return this._proxyPrefixEnabled;
    }

    isDiff(other: this): boolean {
        // How is this for a short cut!
        return !_.isEqual(this.toJS(), other.toJS());
    }

    clone(): this {
        return new ImsImageryProvider(this.toJS(), this._imagMagOptionsHelper) as any;
    }

    set name(value: string) {
        this._name = value;
    }

    set type(value: string) {
        if (value !== this._type) {
            // populate required parameters accordingly.
            this._requiredParameters = this._createRequiredParamsForType(value);

            // Clear all other parameters!
            this._optionalParameters = [];
            this._customParameters = [];
            this._service = undefined;
        }
        this._type = value;
    }

    set service(value: MapService) {
        this._service = value;
    }

    set hideInMapLayers(value: boolean) {
        this._hideInMapLayers = value ? true : false;
    }

    set showByDefault(value: boolean) {
        this._showByDefault = value;
    }

    get requiredParameters(): ImageryProviderParameter[] {
        return this._requiredParameters;
    }

    get optionalParameters(): ImageryProviderParameter[] {
        return this._optionalParameters;
    }

    get customParameters(): ImageryProviderParameter[] {
        return this._customParameters;
    }

    /**
     * Set a standard parameter value. Used for required and optional parameters.
     * @param {string} key
     * @param value
     */
    setStdParameterValue(key: string, value: any, childProps: any = null) {
        const param = this.getStdParameterByKey(key);
        // Sanity check it. The clients of this object should pass the right things in!
        if (!_.isNil(value)) {
            if (param.type === ImageryProviderParameterType.ARRAY) {
                if (!Array.isArray(value)) {
                    throw new Error(`Trying to set ${key} to be a non array type, ${value}`);
                }
            } else if (param.type === ImageryProviderParameterType.NUMBER) {
                if (!Number.isFinite(value)) {
                    throw new Error(`Trying to set ${key} to a non finite number, ${value}`);
                }
            } else if (param.type === ImageryProviderParameterType.STRING) {
                if (!_.isString(value)) {
                    throw new Error(`Trying to set ${key} to a non string, ${value}`);
                }
            }
        }
        // mutate it
        param.value = value;

        // hack to handle dropdown values
        if (childProps) {
            if (!param.childProps) {
                param.childProps = {};
            }

            param.childProps = Object.assign(param.childProps, childProps);
        }
    }

    setCustomParameterKey(fieldId: string, key: string): void {
        this.getCustomParameterByFieldId(fieldId).key = key;
    }

    setCustomParameterValue(fieldId: string, value: any): void {
        this.getCustomParameterByFieldId(fieldId).value = value;
    }

    /**
     * A standard parameter is either a required or optional parameter.
     * This does NOT work for custom parameters
     *
     * This will return the first parameter with the matching key.
     *
     *
     * @param {string} key
     * @returns {T}
     */
    getStdParameterByKey(key: string) {
        const allParams = this._requiredParameters.concat(this._optionalParameters);

        const result = allParams.find((p) => p.key === key);
        if (!result) {
            if (this._customParameters.find((p) => p.key === key)) {
                // sanity check
                throw new Error(
                    `You seem to be using getParameterByKey to find a custom param for key ${key}. You should be using getParamByFieldId instead!`,
                );
            } else {
                throw new Error(`Could not find param for key ${key}`);
            }
        }
        return result;
    }

    addOptionalParameter(key: string, value?: any): void {
        if (this.service && this.service.hasOptionalParameter(key)) {
            this._optionalParameters.push({
                key,
                value,
                type: ImageryProviderParameterType.IMS_DROPDOWN,
                childProps: {
                    options: this.service.getOptionalParameter(key),
                },
            });
        } else {
            // find the key
            if (this.optionalParameters.find((p) => p.key === key)) {
                throw new Error(`Already have optional param for key ${key}`);
            }
            const paramDef = this._imagMagOptionsHelper.getOptionalParamByKey(this._type, key);

            let value;

            if (paramDef.type === 'boolean') {
                // Allow valid falsey boolean defaults
                // also, if it's a boolean value we probably don't want to allow a 'null' because it shows
                // as an unchecked checkbox but also has a null value. If the user actually wants a 'false',
                // they need to click the check box twice. This doesn't make sense so just default to false
                // is the defaultValue is null or undefined.
                value = !!paramDef.defaultValue;
            } else {
                value = paramDef.defaultValue || '';
            }

            this._optionalParameters.push({
                key: key,
                value: value,
                type: paramDef.type,
            });
        }
    }

    setOptionalParameterKey(key: string, newKey: string): void {
        const p = this.getStdParameterByKey(key);
        p.key = newKey;
        const paramDef = this._imagMagOptionsHelper.getOptionalParamByKey(this._type, newKey);
        p.type = paramDef.type;
        if (!_.isNil(paramDef.defaultValue)) {
            p.value = paramDef.defaultValue;
        } else {
            switch (p.type) {
                case ImageryProviderParameterType.STRING:
                    p.value = '';
                    break;
                case ImageryProviderParameterType.NUMBER:
                    p.value = 0;
                    break;
                case ImageryProviderParameterType.BOOLEAN:
                    p.value = false;
                    break;
                case ImageryProviderParameterType.URL:
                    p.value = '';
                    break;
                case ImageryProviderParameterType.ARRAY:
                    p.value = [];
                    break;
                default:
                    throw new Error(`param type not handled ${p.type}`);
            }
            p.value = '';
        }
    }

    removeOptionalParameter(key: string): void {
        this._optionalParameters = this._optionalParameters.filter((p) => p.key !== key);
    }

    addCustomParameter(key: string): void {
        this._customParameters.push(ImsImageryProvider._createCustomParameter(key));
    }

    private static _createCustomParameter(
        key: string,
        value: string = '',
        type: ImageryProviderParameterType = ImageryProviderParameterType.STRING,
    ): ImageryProviderParameter {
        return {
            key,
            value,
            type,
            fieldId: generateUUID(),
        };
    }

    removeCustomParameter(fieldId: string): void {
        this._customParameters = this._customParameters.filter((p) => p.fieldId !== fieldId);
    }

    /**
     * More for testing than anything else...
     * @param {string} key
     */
    getParameterValue(key: string): any {
        return this.getStdParameterByKey(key).value;
    }

    getCustomParameterValueByFieldId(fieldId: string): any {
        return this.getCustomParameterByFieldId(fieldId).value;
    }

    getCustomParameterByFieldId(fieldId: string): ImageryProviderParameter {
        const result = this._customParameters.find((p) => p.fieldId === fieldId);
        if (!result) {
            throw new Error(`Could not find custom field for fieldId ${fieldId}`);
        }
        return result;
    }

    /**
     * Create something cesium friendly for the BaseImageryLayersService
     * Pls excuse the crappy name
     *
     * @returns {any}
     */
    toBaseImageryLayersServiceLayerObj(): BaseImageryLayersServiceLayerObj {
        const result: BaseImageryLayersServiceLayerObj = this._optionalParameters
            .concat(this._requiredParameters)
            .reduce((accum, p) => {
                accum[p.key] = p.value;
                return accum;
            }, {}) as any;

        result.name = this.name;
        result.type = this.type;
        result.queryParameters = this.toJS().queryParameters;
        result.uuid = this.uuid;
        result.hideInMapLayers = this.hideInMapLayers;
        // Not used by cesium, but used by BaseImageryLayerService and ImsImageryLayersService
        // to work out whether to show the layer by default
        result.showByDefault = this.showByDefault;
        result.proxyPrefixUrl = this.proxyPrefixUrl;

        return result;
    }

    private _createRequiredParamsForType(type: string) {
        return this._imagMagOptionsHelper
            .getRequiredParams(type)
            .map((p: LayerParameterDefinition) => {
                return {
                    key: p.mapParam,
                    value: p.defaultValue ? p.defaultValue : '',
                    type: p.type as any,
                };
            });
    }
}

function parameterReducer(accum: {}, param: ImageryProviderParameter) {
    accum[param.key] = param.value;
    return accum;
}
